import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box } from '../box';
import { Typography } from '../typography';
import { Button } from '../button';
import { Plus, Trash } from '@otp/icons';
import { GroupContainer, ConditionRow, OperatorSelect, ItemSelect, DeleteButton, } from './styles';
export const validateDropdownConditionalCondition = (condition) => {
    if ('operator' in condition) {
        return condition.conditions.every(validateDropdownConditionalCondition);
    }
    return Array.isArray(condition.value)
        ? condition.value.length > 0
        : !!condition.value;
};
const generateExplanation = (conditionGroup, options) => {
    var _a;
    const getOptionName = (value) => {
        var _a;
        if (Array.isArray(value)) {
            return value
                .map((v) => { var _a; return ((_a = options.find((opt) => opt.value === v)) === null || _a === void 0 ? void 0 : _a.name) || v; })
                .join(', ');
        }
        return ((_a = options.find((opt) => opt.value === value)) === null || _a === void 0 ? void 0 : _a.name) || value;
    };
    const explainCondition = (condition) => {
        var _a;
        if ('operator' in condition) {
            const conditions = condition.conditions.map(explainCondition);
            return `(${conditions.join(` <b>${(_a = condition.operator) === null || _a === void 0 ? void 0 : _a.toLowerCase()}</b> `)})`;
        }
        return getOptionName(condition.value);
    };
    const conditions = conditionGroup.conditions.map(explainCondition);
    return conditions.join(` <b>${(_a = conditionGroup.operator) === null || _a === void 0 ? void 0 : _a.toLowerCase()}</b> `);
};
export const DropdownConditional = ({ multiple, value, defaultValue, onChange, options, showExplanation, explanationPrefix, topLevelOperators = ['AND'], nestedLevelOperators = ['OR'], nestedConditionMaxItems, limitTags, selectClassName, error, }) => {
    const [state, setState] = useState(() => {
        if (defaultValue) {
            return defaultValue;
        }
        return {
            operator: (topLevelOperators === null || topLevelOperators === void 0 ? void 0 : topLevelOperators[0]) || 'AND',
            conditions: [],
        };
    });
    // Update state when value prop changes
    useEffect(() => {
        if (value) {
            setState(value);
        }
    }, [value]);
    const conditions = state.conditions || [];
    const handleOperatorChange = (operator) => {
        const newState = Object.assign(Object.assign({}, state), { operator });
        setState(newState);
        onChange === null || onChange === void 0 ? void 0 : onChange(newState);
    };
    const handleAddCondition = () => {
        const newState = Object.assign(Object.assign({}, state), { conditions: [...conditions, { value: multiple ? [] : '' }] });
        setState(newState);
        onChange === null || onChange === void 0 ? void 0 : onChange(newState);
    };
    const handleAddGroup = () => {
        const newGroup = {
            operator: 'OR',
            conditions: [{ value: multiple ? [] : '' }],
        };
        const newState = Object.assign(Object.assign({}, state), { conditions: [...conditions, newGroup] });
        setState(newState);
        onChange === null || onChange === void 0 ? void 0 : onChange(newState);
    };
    const handleRemoveCondition = (conditionIndex, itemIndex) => {
        const newState = Object.assign(Object.assign({}, state), { conditions: conditions
                .map((condition, i) => {
                if (i === conditionIndex) {
                    if ('operator' in condition) {
                        // If it's a group, remove the item at itemIndex
                        if (itemIndex !== undefined) {
                            return Object.assign(Object.assign({}, condition), { conditions: condition.conditions.filter((_, j) => j !== itemIndex) });
                        }
                        // If no itemIndex provided, remove the entire group
                        return null;
                    }
                    else {
                        // If it's a simple condition, remove it
                        return null;
                    }
                }
                return condition;
            })
                .filter(Boolean) });
        setState(newState);
        onChange === null || onChange === void 0 ? void 0 : onChange(newState);
    };
    const handleConditionChange = (conditionIndex, itemIndex, value) => {
        const newState = Object.assign(Object.assign({}, state), { conditions: conditions.map((condition, i) => {
                if (i === conditionIndex) {
                    if ('operator' in condition) {
                        // If it's a group, update the item at itemIndex
                        return Object.assign(Object.assign({}, condition), { conditions: condition.conditions.map((subCondition, j) => {
                                if (j === itemIndex) {
                                    return Object.assign(Object.assign({}, subCondition), { value });
                                }
                                return subCondition;
                            }) });
                    }
                    else {
                        // If it's a simple condition, update it
                        return Object.assign(Object.assign({}, condition), { value });
                    }
                }
                return condition;
            }) });
        setState(newState);
        onChange === null || onChange === void 0 ? void 0 : onChange(newState);
    };
    const handleGroupChange = (groupIndex, updatedGroup) => {
        const updateNestedGroup = (condition) => {
            if ('operator' in condition) {
                return Object.assign(Object.assign({}, condition), { conditions: condition.conditions.map((subCondition, subIndex) => {
                        if (subIndex === groupIndex) {
                            return updatedGroup;
                        }
                        return updateNestedGroup(subCondition);
                    }) });
            }
            return condition;
        };
        const newState = Object.assign(Object.assign({}, state), { conditions: conditions.map((condition, i) => {
                if (i === groupIndex) {
                    return updatedGroup;
                }
                return updateNestedGroup(condition);
            }) });
        setState(newState);
        onChange === null || onChange === void 0 ? void 0 : onChange(newState);
    };
    const renderOperatorSelector = ({ condition, onChange, availableOperators = topLevelOperators, }) => {
        return (_jsx(OperatorSelect, { disableClearable: true, defaultValue: condition.operator, value: condition.operator, onChange: onChange, options: availableOperators.map((operator) => ({
                value: operator,
                name: operator,
            })) }));
    };
    const renderCondition = (condition, conditionIndex, itemIndex, isNestedCondition = false) => {
        if ('operator' in condition) {
            return (_jsxs(GroupContainer, { children: [_jsxs(ConditionRow, { children: [renderOperatorSelector({
                                condition,
                                onChange: (_event, value) => {
                                    var _a;
                                    const newValue = Array.isArray(value)
                                        ? (_a = value[0]) === null || _a === void 0 ? void 0 : _a.value
                                        : value === null || value === void 0 ? void 0 : value.value;
                                    if (newValue) {
                                        handleGroupChange(conditionIndex, Object.assign(Object.assign({}, condition), { operator: newValue }));
                                    }
                                },
                                availableOperators: nestedLevelOperators,
                            }), _jsx(Button, { variant: "outlined", onClick: () => handleRemoveCondition(conditionIndex), startIcon: _jsx(Trash, {}), children: "Remove Group" })] }), condition.conditions.map((subCondition, subIndex) => renderCondition(subCondition, conditionIndex, subIndex, true)), _jsx(ConditionRow, { children: _jsx(Button, { variant: "outlined", onClick: () => handleGroupChange(conditionIndex, Object.assign(Object.assign({}, condition), { conditions: [
                                    ...condition.conditions,
                                    { value: multiple ? [] : '' },
                                ] })), startIcon: _jsx(Plus, {}), children: "Add Condition" }) })] }, conditionIndex));
        }
        return (_jsxs(ConditionRow, { children: [_jsx(ItemSelect, { className: selectClassName, multiple: multiple, defaultValue: condition.value, value: condition.value, onChange: (_event, value) => {
                        const newValue = Array.isArray(value)
                            ? value === null || value === void 0 ? void 0 : value.map(({ value }) => value)
                            : value === null || value === void 0 ? void 0 : value.value;
                        if (newValue) {
                            const handledNewValue = multiple && isNestedCondition && !!nestedConditionMaxItems
                                ? newValue.slice(-nestedConditionMaxItems)
                                : newValue;
                            handleConditionChange(conditionIndex, itemIndex, handledNewValue);
                        }
                    }, options: options, limitTags: limitTags }), _jsx(DeleteButton, { variant: "outlined", onClick: () => handleRemoveCondition(conditionIndex, itemIndex), startIcon: _jsx(Trash, {}), classes: {
                        startIcon: '!mr-0 !ml-0',
                    } })] }, condition.value));
    };
    return (_jsxs("div", { children: [showExplanation && (conditions === null || conditions === void 0 ? void 0 : conditions.length) > 0 && (_jsx(Box, { mt: 2, pb: 2, bgcolor: "background.default", borderRadius: 1, children: _jsxs(Typography, { variant: "subtitle1", color: "text.secondary", children: [explanationPrefix, ' ', _jsx("span", { dangerouslySetInnerHTML: {
                                __html: generateExplanation(state, options),
                            } })] }) })), _jsx(Box, { mb: 3, children: renderOperatorSelector({
                    condition: state,
                    onChange: (_event, value) => {
                        var _a;
                        const newValue = Array.isArray(value)
                            ? (_a = value[0]) === null || _a === void 0 ? void 0 : _a.value
                            : value === null || value === void 0 ? void 0 : value.value;
                        if (newValue) {
                            handleOperatorChange(newValue);
                        }
                    },
                    availableOperators: topLevelOperators,
                }) }), conditions === null || conditions === void 0 ? void 0 : conditions.map((condition, index) => renderCondition(condition, index, 0)), _jsxs(Box, { mt: 2, display: "flex", gap: 2, children: [_jsx(Button, { variant: "contained", onClick: handleAddCondition, startIcon: _jsx(Plus, {}), children: "Add Condition" }), _jsx(Button, { variant: "outlined", onClick: handleAddGroup, startIcon: _jsx(Plus, {}), children: "Add Group" })] }), error && (_jsx(Box, { mt: 2, pb: 2, bgcolor: "background.default", borderRadius: 1, children: _jsx(Typography, { variant: "subtitle1", color: "error", children: error }) }))] }));
};
export default DropdownConditional;
